import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import {
  ArrowUpRight,
  Box,
  Map as MapIcon,
  Focus,
  Image as ImageIcon,
  Maximize2,
  Minimize2,
  Network,
  Pause,
  Play,
  RotateCcw,
  Search,
  Tags,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react'
import ForceGraph2D, { ForceGraphMethods, NodeObject, LinkObject } from 'react-force-graph-2d'
import { artworkApi } from '@/features/artworks/api/artworkApi'
import type { ConceptGraph as Graph, GraphNode } from '../api/knowledgeApi'
import { connections, filterGraph, isQuery, nodeColor, nodeKind } from './graphModel'
import { defaultGraphTheme, readGraphTheme } from './graphTheme'
import { drawNode, drawConnection, paintPointer } from './graphCanvas'
import {
  layoutForces,
  layoutTargets,
  layoutDescriptions,
  hierarchyRanks,
  type LayoutMode,
} from './graphLayout'
import { lensGraph, semanticGraph, edgeFamily, relationFrom, type GraphLens } from './graphModel'

import { ImmersiveBoundary } from './ImmersiveBoundary'
import type { ImmersiveHandle } from './ImmersiveGraph'
const ImmersiveGraph = lazy(() => import('./ImmersiveGraph'))

type LinkData = { kind: string; weight: number | null; origin?: string }
type Node = NodeObject<GraphNode>
type Link = LinkObject<GraphNode, LinkData>
const endpoint = (n: Link['source']) => (typeof n === 'object' ? String(n.id) : String(n))
const control =
  'inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-border bg-card px-2.5 text-xs text-foreground transition hover:border-foreground/20 hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-35'
/** A local, read-only graph lens. Selecting inspects; opening a node is always explicit. */
export function ConceptGraph({
  graph,
  height = 520,
  onConceptClick,
  labelAll = false,
}: {
  graph: Graph
  height?: number
  onConceptClick?: (ucid: string) => void
  labelAll?: boolean
}) {
  const navigate = useNavigate()
  const wrapRef = useRef<HTMLDivElement>(null)
  const expandButtonRef = useRef<HTMLButtonElement>(null)
  const fgRef = useRef<ForceGraphMethods<Node, Link>>()
  const immersiveRef = useRef<ImmersiveHandle>(null)
  const [view, setView] = useState<'2d' | '3d'>('2d')
  const [, refreshImages] = useState(0)
  const imageCache = useRef(new Map<string, HTMLImageElement>())
  const [theme, setTheme] = useState(defaultGraphTheme)
  const [width, setWidth] = useState(800)
  const [viewportHeight, setViewportHeight] = useState(800)
  const [hover, setHover] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const [category, setCategory] = useState('')
  const [showArtworks, setShowArtworks] = useState(true)
  const [labels, setLabels] = useState(labelAll)
  const [layout, setLayout] = useState<LayoutMode>('force')
  const [lens, setLens] = useState<GraphLens>('all')
  const [focus, setFocus] = useState(true)
  const [layoutVersion, setLayoutVersion] = useState(0)
  const [search, setSearch] = useState('')
  const [fullscreen, setFullscreen] = useState(false)
  const [hoveredLink, setHoveredLink] = useState<Link | null>(null)
  const [animate, setAnimate] = useState(
    () =>
      !(
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      )
  )

  const canvasHeight = fullscreen
    ? Math.max(180, viewportHeight - (view === '3d' ? 244 : 260))
    : height
  const sideBySide = width >= 900
  const canvasWidth = Math.max(1, width - (sideBySide ? 252 : 0))

  useEffect(() => {
    const read = () => {
      if (wrapRef.current) setTheme(readGraphTheme(wrapRef.current))
    }
    read()
    const observer = new MutationObserver(read)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'style'],
    })
    return () => observer.disconnect()
  }, [fullscreen, view])

  useEffect(() => {
    const el = wrapRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) =>
      setWidth(Math.max(1, Math.floor(entry.contentRect.width)))
    )
    ro.observe(el)
    return () => ro.disconnect()
  }, [fullscreen])
  useEffect(() => {
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!media) return
    const change = () => {
      if (media.matches) setAnimate(false)
    }
    media.addEventListener('change', change)
    return () => media.removeEventListener('change', change)
  }, [])
  useEffect(() => {
    const resize = () => setViewportHeight(window.innerHeight)
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])
  useEffect(() => {
    if (!fullscreen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    expandButtonRef.current?.focus()
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setFullscreen(false)
      }
      if (event.key === 'Tab') {
        const controls = wrapRef.current?.querySelectorAll<HTMLElement>(
          'button:not(:disabled), input:not(:disabled), select:not(:disabled), a[href]'
        )
        if (!controls?.length) return
        const first = controls[0],
          last = controls[controls.length - 1]
        if (!wrapRef.current?.contains(document.activeElement)) {
          event.preventDefault()
          ;(event.shiftKey ? last : first).focus()
          return
        }
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', keydown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', keydown)
      requestAnimationFrame(() => expandButtonRef.current?.focus())
    }
  }, [fullscreen])
  useEffect(() => {
    setSelected(null)
    setHover(null)
    setSearch('')
    setCategory('')
  }, [graph.centre])

  const visible = useMemo(
    () => lensGraph(filterGraph(semanticGraph(graph), category, showArtworks), lens),
    [graph, category, showArtworks, lens]
  )
  const adjacent = useMemo(() => connections(visible), [visible])
  const categories = useMemo(
    () =>
      [
        ...new Set(
          graph.nodes
            .filter((n) => n.kind === 'concept' && !isQuery(n))
            .map((n) => n.category || 'uncategorised')
        ),
      ].sort(),
    [graph]
  )
  // The simulation mutates coordinates and endpoints; cache objects stay immutable.
  const positionCache = useRef(new Map<string, Node>())
  const data = useMemo(() => {
    const targets = layoutTargets(visible, layout)
    const nodes = visible.nodes.map((n) => {
      const previous = positionCache.current.get(n.id),
        target = targets.get(n.id)!
      return { ...n, x: previous?.x ?? target.x, y: previous?.y ?? target.y }
    }) as Node[]
    positionCache.current = new Map(nodes.map((n) => [n.id, n]))
    return { nodes, links: visible.edges.map((e) => ({ ...e })) as Link[] }
  }, [visible, layout, layoutVersion])
  const ranks = useMemo(() => hierarchyRanks(visible), [visible])
  const targets = useMemo(() => layoutTargets(visible, layout), [visible, layout])
  const settled = useRef(false)
  useEffect(() => {
    if (view !== '2d') return
    settled.current = false
    const fg = fgRef.current
    const forces = layoutForces(visible, layout)
    fg?.d3Force('collision', forces.collision)
    fg?.d3Force('x', forces.x)
    fg?.d3Force('y', forces.y)
    fg?.d3Force('charge')?.strength(layout === 'force' ? -120 : 0)
    fg?.d3Force('link')
      ?.distance((l: Link) => (l.kind === 'embodies' ? 210 : 180))
      .strength(layout === 'force' ? 0.16 : 0)
    fg?.d3ReheatSimulation()
    // Fit the destination as well as the current positions before the nodes travel there.
    if (!data.nodes.length) return
    const points = [...data.nodes, ...(layout === 'force' ? [] : targets.values())]
    const minX = Math.min(...points.map((n) => n.x || 0)),
      maxX = Math.max(...points.map((n) => n.x || 0))
    const minY = Math.min(...points.map((n) => n.y || 0)),
      maxY = Math.max(...points.map((n) => n.y || 0))
    const frame = requestAnimationFrame(() => {
      fg?.centerAt((minX + maxX) / 2, (minY + maxY) / 2, 0)
      fg?.zoom(
        Math.max(
          0.2,
          Math.min(
            1.2,
            (canvasWidth - 108) / (maxX - minX + 180),
            (canvasHeight - 108) / (maxY - minY + 180)
          )
        ),
        0
      )
    })
    return () => cancelAnimationFrame(frame)
  }, [data, fullscreen, layout, visible, canvasWidth, canvasHeight, targets, view])
  useEffect(() => {
    if (animate) fgRef.current?.d3ReheatSimulation()
  }, [animate])

  useEffect(() => {
    setSelected((id) => (id && visible.nodes.some((n) => n.id === id) ? id : null))
    setHover(null)
  }, [visible])

  const activeId = view === '3d' ? selected || hover : hover || selected
  const active = visible.nodes.find((n) => n.id === (selected || hover))
  const lit = (id: string) => !activeId || id === activeId || !!adjacent.get(activeId)?.has(id)
  const linkLit = (l: Link) =>
    !!activeId && (endpoint(l.source) === activeId || endpoint(l.target) === activeId)
  const matches = search.trim()
    ? visible.nodes
        .filter((n) =>
          `${n.name} ${n.artistName || ''}`.toLowerCase().includes(search.trim().toLowerCase())
        )
        .slice(0, 8)
    : []
  const concepts = visible.nodes.filter((n) => n.kind === 'concept' && !isQuery(n)).length
  const artworks = visible.nodes.filter((n) => n.kind === 'artwork').length
  const neighbours = active ? visible.nodes.filter((n) => adjacent.get(active.id)?.has(n.id)) : []

  const select = (id: string) => {
    setSelected(id)
    setSearch('')
    setHover(null)
    if (view === '3d') {
      immersiveRef.current?.focus(id)
      return
    }
    const n = data.nodes.find((node) => node.id === id)
    if (n && Number.isFinite(n.x) && Number.isFinite(n.y)) {
      fgRef.current?.centerAt(n.x, n.y, animate ? 550 : 0)
      fgRef.current?.zoom(Math.max(fgRef.current.zoom(), 1.25), animate ? 550 : 0)
    }
  }
  const clear = () => {
    immersiveRef.current?.cancelJourney()
    setSelected(null)
    setHover(null)
    setSearch('')
  }
  const fit = () =>
    view === '3d' ? immersiveRef.current?.fit() : fgRef.current?.zoomToFit(animate ? 500 : 0, 54)
  const zoom = (factor: number) =>
    view === '3d'
      ? immersiveRef.current?.zoom(factor)
      : fgRef.current?.zoom(fgRef.current.zoom() * factor, animate ? 250 : 0)
  const thumbnailUrl = (n: GraphNode) =>
    n.imageUrl || (n.artworkId ? artworkApi.getArtworkImageUrl(n.artworkId) : '')
  const image = (n: GraphNode) => {
    const url = thumbnailUrl(n)
    if (!url) return undefined
    if (!imageCache.current.has(url)) {
      const img = new window.Image()
      img.onload = () => refreshImages((count) => count + 1)
      img.src = url
      imageCache.current.set(url, img)
    }
    return imageCache.current.get(url)
  }
  const open = (n: GraphNode) => {
    if (n.kind === 'concept' && n.ucid) {
      if (onConceptClick) onConceptClick(n.ucid)
      else navigate(`/app/knowledge/${n.ucid}`)
    } else if (n.kind === 'artwork' && n.artworkId) navigate(`/app/artworks/${n.artworkId}`)
  }

  const content = (
    <div
      ref={wrapRef}
      role={fullscreen ? 'dialog' : 'region'}
      aria-modal={fullscreen || undefined}
      className={`${view === '3d' ? 'dark' : ''} ${fullscreen ? 'fixed inset-3 z-[100] ring-[16px] ring-background/90' : 'relative'} overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-card text-card-foreground shadow-card`}
      aria-label="Interactive relationship graph"
    >
      <div
        className={`flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 ${view === '3d' ? 'py-2' : 'py-4'}`}
      >
        <div className="flex items-center gap-3">
          <span
            className={`rounded-xl border border-arthaus-gold/20 p-2 ${view === '3d' ? 'bg-arthaus-gold/10 text-arthaus-gold' : 'bg-arthaus-gold-light text-foreground'}`}
          >
            <Network className="h-4 w-4" />
          </span>
          <div>
            <h3 className="font-serif text-lg font-semibold tracking-[.12em] text-foreground">
              ARTHAUS
            </h3>
            <p className="mt-0.5 text-[10px] uppercase tracking-[.18em] text-muted-foreground">
              Knowledge, in context
            </p>
          </div>
        </div>
        <div
          className="flex items-center gap-1 rounded-lg border border-border bg-muted/50 p-1"
          role="group"
          aria-label="Graph view"
        >
          <button
            className={`${control} ${view === '2d' ? 'bg-card shadow-sm' : 'border-transparent bg-transparent'}`}
            aria-pressed={view === '2d'}
            onClick={() => setView('2d')}
          >
            <MapIcon className="h-3.5 w-3.5" /> 2D map
          </button>
          <button
            className={`${control} ${view === '3d' ? 'border-arthaus-gold/50 bg-arthaus-gold/15 text-arthaus-gold' : 'border-transparent bg-transparent'}`}
            aria-pressed={view === '3d'}
            onClick={() => {
              setView('3d')
              setFullscreen(true)
            }}
          >
            <Box className="h-3.5 w-3.5" /> Immersive 3D
          </button>
        </div>
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[.14em] text-muted-foreground">
          <span
            className={`h-1.5 w-1.5 rounded-full ${animate ? 'bg-arthaus-gold motion-safe:animate-pulse' : 'bg-muted-foreground'}`}
          />
          {animate ? 'Motion on' : 'Still view'}
          <span className="ml-2 text-muted-foreground">
            {visible.nodes.length} {visible.nodes.length === 1 ? 'node' : 'nodes'} ·{' '}
            {visible.edges.length} {visible.edges.length === 1 ? 'link' : 'links'}
          </span>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-3">
        <label className="flex items-center gap-2 text-[10px] text-muted-foreground">
          Layout
          <select
            aria-label="Graph layout"
            value={layout}
            onChange={(e) => setLayout(e.target.value as LayoutMode)}
            className="h-9 rounded-lg border border-border bg-card px-2 text-xs text-foreground"
          >
            <option value="force">Force directed</option>
            <option value="categories">Category clusters</option>
            <option value="hierarchy">Ontology hierarchy</option>
          </select>
        </label>
        <div className="relative min-w-[160px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            aria-label="Find a node"
            placeholder="Find a node…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && matches[0]) select(matches[0].id)
              if (e.key === 'Escape') setSearch('')
            }}
            className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-ring"
          />
          {search.trim() && (
            <div
              className="absolute left-0 right-0 top-11 z-30 max-h-64 overflow-auto rounded-xl border border-border bg-popover text-popover-foreground p-1.5 shadow-2xl"
              aria-label="Matching nodes"
            >
              {matches.length ? (
                matches.map((n) => (
                  <button
                    type="button"
                    key={n.id}
                    onClick={() => select(n.id)}
                    className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-accent"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: nodeColor(n, theme) }}
                    />
                    <span className="truncate">{n.name}</span>
                    <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                      {nodeKind(n)}
                    </span>
                  </button>
                ))
              ) : (
                <p className="p-3 text-xs text-muted-foreground">No matching nodes in this view.</p>
              )}
            </div>
          )}
        </div>
        <button className={control} title="Zoom in" aria-label="Zoom in" onClick={() => zoom(1.35)}>
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button
          className={control}
          title="Zoom out"
          aria-label="Zoom out"
          onClick={() => zoom(1 / 1.35)}
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <button
          className={control}
          onClick={() => setLayoutVersion((v) => v + 1)}
          title="Release pinned nodes and relax this layout"
        >
          <RotateCcw className="h-3.5 w-3.5" /> Reflow
        </button>
        <button className={control} onClick={fit} title="Fit graph">
          <Focus className="h-3.5 w-3.5" />
          Fit
        </button>
        <button
          className={control}
          aria-pressed={animate}
          onClick={() => setAnimate(!animate)}
          title={animate ? 'Pause animation' : 'Animate graph'}
        >
          {animate ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          <span className="sr-only">Animation</span>
        </button>
        <button
          className={control}
          aria-label={fullscreen ? 'Exit fullscreen' : 'Expand graph'}
          ref={expandButtonRef}
          onClick={() => setFullscreen(!fullscreen)}
        >
          {fullscreen ? (
            <Minimize2 className="h-3.5 w-3.5" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" />
          )}
        </button>
      </div>

      <div
        className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-2"
        role="group"
        aria-label="Relationship lens"
      >
        {(
          [
            ['all', 'All connections'],
            ['ontology', 'Ontology'],
            ['similarity', 'Similarity'],
            ['artwork', 'Artwork evidence'],
            ...(graph.nodes.some(isQuery) ? [['search', 'Search context'] as const] : []),
          ] as const
        ).map(([value, label]) => (
          <button
            key={value}
            className={`${control} ${lens === value ? (view === '3d' ? 'border-arthaus-gold/50 bg-arthaus-gold/15 text-arthaus-gold' : 'border-arthaus-gold/50 bg-arthaus-gold-light') : 'border-transparent bg-transparent'}`}
            aria-pressed={lens === value}
            onClick={() => {
              clear()
              setLens(value)
            }}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto px-2 text-[10px] text-muted-foreground">
          Solid = ontology/evidence · dashed = similarity/search
        </span>
      </div>
      <div className={sideBySide ? 'flex' : ''}>
        <div className="relative min-w-0" style={{ width: canvasWidth, height: canvasHeight }}>
          {view === '3d' ? (
            <ImmersiveBoundary onReturn={() => setView('2d')}>
              <Suspense
                fallback={
                  <div
                    className="flex h-full items-center justify-center bg-muted font-serif text-lg"
                    role="status"
                  >
                    Opening the immersive gallery…
                  </div>
                }
              >
                <ImmersiveGraph
                  ref={immersiveRef}
                  graph={visible}
                  width={canvasWidth}
                  height={canvasHeight}
                  theme={theme}
                  layout={layout}
                  generation={layoutVersion}
                  animate={animate}
                  labels={labels}
                  activeId={activeId}
                  focusConnections={focus}
                  onSelect={select}
                  onHover={setHover}
                  onClear={clear}
                  onReturn={() => setView('2d')}
                />
              </Suspense>
            </ImmersiveBoundary>
          ) : (
            <ForceGraph2D<GraphNode, LinkData>
              ref={fgRef}
              graphData={data}
              width={canvasWidth}
              height={canvasHeight}
              backgroundColor={theme.background}
              warmupTicks={animate ? 20 : 240}
              cooldownTicks={animate ? 220 : 0}
              d3VelocityDecay={0.35}
              autoPauseRedraw={!animate}
              minZoom={0.2}
              maxZoom={8}
              enableZoomInteraction={fullscreen}
              onEngineStop={() => {
                if (!settled.current) {
                  settled.current = true
                  if (!selected && !hover) fit()
                }
              }}
              onRenderFramePre={(ctx, scale) => {
                ctx.save()
                ctx.fillStyle = theme.foreground + '12'
                // A quiet dot field remains anchored to graph space while panning.
                const x = fgRef.current?.screen2GraphCoords(0, 0),
                  y = fgRef.current?.screen2GraphCoords(canvasWidth, canvasHeight)
                const step = Math.max(32, 32 / scale)
                if (x && y)
                  for (let i = Math.ceil(x.x / step) * step; i < y.x; i += step)
                    for (let j = Math.ceil(x.y / step) * step; j < y.y; j += step) {
                      ctx.beginPath()
                      ctx.arc(i, j, 0.65 / scale, 0, Math.PI * 2)
                      ctx.fill()
                    }
                if (layout !== 'force') {
                  const groups = new Map<string, Node[]>()
                  for (const n of data.nodes) {
                    const group = targets.get(n.id)!.group
                    if (!groups.has(group)) groups.set(group, [])
                    groups.get(group)!.push(n)
                  }
                  for (const [group, nodes] of groups) {
                    const artworkGroup = group === 'Artworks'
                    const left =
                      Math.min(...nodes.map((n) => (n.x || 0) - (artworkGroup ? 70 : 85))) - 18
                    const top =
                      Math.min(...nodes.map((n) => (n.y || 0) - (artworkGroup ? 85 : 35))) - 24
                    const right =
                      Math.max(...nodes.map((n) => (n.x || 0) + (artworkGroup ? 70 : 85))) + 18
                    const bottom =
                      Math.max(...nodes.map((n) => (n.y || 0) + (artworkGroup ? 85 : 35))) + 18
                    ctx.fillStyle = theme.card + '80'
                    ctx.strokeStyle = theme.border + '90'
                    ctx.lineWidth = 1 / scale
                    ctx.beginPath()
                    ctx.roundRect(left, top, right - left, bottom - top, 18)
                    ctx.fill()
                    ctx.stroke()
                    ctx.font = '500 13px Inter, system-ui, sans-serif'
                    ctx.fillStyle = theme.mutedForeground
                    ctx.fillText(
                      `${group.replace(/_/g, ' ').toUpperCase()} · ${nodes.length}`,
                      left + 16,
                      top + 19
                    )
                  }
                }
                ctx.restore()
              }}
              nodeId="id"
              nodeLabel={() => ''}
              linkCanvasObjectMode={() => 'replace'}
              linkCanvasObject={(l, ctx, scale) =>
                drawConnection(l, ctx, scale, {
                  theme,
                  active: linkLit(l) || hoveredLink === l,
                  dim: !!activeId && focus && !linkLit(l),
                  labels,
                })
              }
              linkDirectionalParticles={0}
              onLinkHover={(l) => setHoveredLink(l)}
              onBackgroundClick={clear}
              onNodeHover={(n) => setHover(n ? String(n.id) : null)}
              onNodeClick={(n) => select(String(n.id))}
              onNodeDragEnd={(n) => {
                n.fx = n.x
                n.fy = n.y
              }}
              nodeCanvasObject={(n, ctx, scale) =>
                drawNode(n, ctx, scale, {
                  theme,
                  active: activeId === n.id,
                  dim: focus && !lit(n.id),
                  labels,
                  animate,
                  image,
                })
              }
              nodePointerAreaPaint={paintPointer}
            />
          )}
          <div
            className="pointer-events-none absolute left-3 right-3 top-3 flex flex-col items-start gap-1"
            role="status"
          >
            {layout === 'hierarchy' && !ranks.size && (
              <p className="rounded-md border border-arthaus-gold/40 bg-card/95 px-3 py-2 text-xs">
                No broader/variant assertions in this neighbourhood. Concepts are unranked.
              </p>
            )}
            {!visible.edges.length && (
              <p className="rounded-md border border-border bg-card/95 px-3 py-2 text-xs">
                No {lens === 'all' ? '' : lens} connections in this neighbourhood. Try another lens.
              </p>
            )}
          </div>
          {view === '2d' && (
            <div className="pointer-events-none absolute bottom-3 left-4 right-4 flex justify-between gap-3 text-[10px] text-muted-foreground">
              <span>
                {hoveredLink
                  ? `${relationFrom(hoveredLink.kind, true)}${edgeFamily(hoveredLink.kind) === 'similarity' && hoveredLink.weight != null ? ` · ${Math.round(hoveredLink.weight * 100)}% similarity` : ''}`
                  : layoutDescriptions[layout]}
              </span>
              <span>
                {fullscreen ? 'Scroll to zoom · Esc to exit' : 'Expand for scroll-to-zoom'}
              </span>
            </div>
          )}
        </div>

        <aside
          className={`min-w-0 border-border bg-muted/30 ${sideBySide ? 'w-[252px] shrink-0 border-l' : 'border-t'}`}
          style={sideBySide ? { maxHeight: canvasHeight, overflowY: 'auto' } : undefined}
          aria-label="Node inspector"
        >
          {active ? (
            <div className="space-y-4 p-4">
              <div className="flex items-start justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[.16em] text-muted-foreground">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: nodeColor(active, theme) }}
                  />
                  {nodeKind(active)}
                </span>
                <button
                  aria-label="Clear selection"
                  className="text-muted-foreground hover:text-foreground"
                  onClick={clear}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {active.kind === 'artwork' && thumbnailUrl(active) && (
                <img
                  src={thumbnailUrl(active)}
                  alt={active.name}
                  className="max-h-32 w-full rounded-lg bg-muted object-contain"
                />
              )}
              <h4 className="break-words text-base font-medium leading-snug text-foreground">
                {active.name}
              </h4>
              {active.artistName && (
                <p className="text-xs text-muted-foreground">{active.artistName}</p>
              )}
              <div className="flex gap-4 text-xs text-muted-foreground">
                <span>
                  <b className="text-foreground">{neighbours.length}</b> connections here
                </span>
                {active.artworkCount != null && (
                  <span>
                    <b className="text-foreground">{active.artworkCount.toLocaleString()}</b>{' '}
                    artworks
                  </span>
                )}
              </div>
              {(active.ucid || active.artworkId) && (
                <button className={`${control} w-full`} onClick={() => open(active)}>
                  {active.kind === 'artwork'
                    ? 'Open artwork'
                    : onConceptClick
                      ? 'Explore neighbourhood'
                      : 'Open concept'}
                  <ArrowUpRight className="ml-auto h-3.5 w-3.5" />
                </button>
              )}
              <div>
                <p className="mb-2 text-[10px] uppercase tracking-[.16em] text-muted-foreground">
                  Connected nodes
                </p>
                <div className="max-h-52 space-y-1 overflow-auto">
                  {neighbours.map((n) => (
                    <button
                      type="button"
                      key={n.id}
                      onClick={() => select(n.id)}
                      className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-foreground hover:bg-accent hover:text-foreground"
                    >
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: nodeColor(n, theme) }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{n.name}</span>
                        <span className="mt-0.5 block text-[9px] text-muted-foreground">
                          {visible.edges
                            .filter(
                              (e) =>
                                (e.source === active.id && e.target === n.id) ||
                                (e.target === active.id && e.source === n.id)
                            )
                            .map(
                              (e) =>
                                `${relationFrom(e.kind, e.source === active.id)}${e.origin ? ` · ${e.origin}` : ''}`
                            )
                            .join(' / ')}
                        </span>
                      </span>
                    </button>
                  ))}
                  {!neighbours.length && (
                    <p className="text-xs text-muted-foreground">
                      No connections in the current filters.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-5 p-5">
              <p className="text-[10px] uppercase tracking-[.18em] text-muted-foreground">
                Follow your curiosity
              </p>
              <h4 className="text-lg font-light leading-snug text-foreground">
                Every artwork has
                <br />a world around it.
              </h4>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Select a node to discover its connections. Follow a neighbour, or open its full
                story.
              </p>
              <div className="grid grid-cols-2 gap-3 border-y border-border py-4">
                <div>
                  <Tags className="mb-2 h-4 w-4 text-foreground" />
                  <p className="text-xl text-foreground">{concepts}</p>
                  <p className="text-[10px] text-muted-foreground">concepts</p>
                </div>
                <div>
                  <ImageIcon className="mb-2 h-4 w-4 text-arthaus-gold" />
                  <p className="text-xl text-foreground">{artworks}</p>
                  <p className="text-[10px] text-muted-foreground">artworks</p>
                </div>
              </div>
              {categories.length ? (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-[.16em] text-muted-foreground">
                    Color is category
                  </p>
                  {categories.map((c) => (
                    <button
                      key={c}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        clear()
                        setCategory(c)
                      }}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{
                          background: nodeColor(
                            { id: '', kind: 'concept', name: '', category: c },
                            theme
                          ),
                        }}
                      />
                      {c.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] leading-relaxed text-muted-foreground">
                  This answer includes no concept matches. The lines connect the query to its
                  returned artworks.
                </p>
              )}
            </div>
          )}
        </aside>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border px-4 py-3 text-[11px] text-muted-foreground">
        <label className="flex items-center gap-2">
          Category
          <select
            aria-label="Graph category"
            value={category}
            onChange={(e) => {
              clear()
              setCategory(e.target.value)
            }}
            className="max-w-[160px] rounded border border-border bg-card px-2 py-1 text-xs text-foreground"
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={showArtworks}
            onChange={(e) => {
              clear()
              setShowArtworks(e.target.checked)
            }}
            className="accent-primary"
          />
          Artworks
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={labels}
            onChange={(e) => setLabels(e.target.checked)}
            className="accent-primary"
          />
          Relation labels
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={focus}
            onChange={(e) => setFocus(e.target.checked)}
            className="accent-primary"
          />
          Focus connections
        </label>
        <button
          className="ml-auto inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
          onClick={() => {
            clear()
            setCategory('')
            setLens('all')
            setShowArtworks(true)
            setLabels(labelAll)
            setFocus(true)
            setLayoutVersion((version) => version + 1)
          }}
        >
          <RotateCcw className="h-3 w-3" />
          Reset view
        </button>
      </div>
    </div>
  )
  return fullscreen ? createPortal(content, document.body) : content
}
